// components/MessageToast.tsx
import React from 'react';
import { Snackbar, Alert, AlertColor, Link, Slide, SlideProps } from '@mui/material';
import { keyframes } from '@mui/system';

// Create a simple pop animation keyframe
const popAnimation = keyframes`
  0% { transform: scale(0.8) translateY(20px); opacity: 0; }
  50% { transform: scale(1.05) translateY(-5px); opacity: 0.8; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

// Slide up transition component
function SlideUpTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export interface MessageToastProps {
  type: AlertColor; // 'success' | 'info' | 'warning' | 'error'
  message: string;
  open: boolean;
  onClose: () => void;
  autoHideDuration?: number;
}

const MessageToast: React.FC<MessageToastProps> = ({
  type,
  message,
  open,
  onClose,
  autoHideDuration = 10000,
}) => {
  // Function to render message with potential links
  const renderMessage = (msg: string) => {
    // Check for various link patterns
    const linkPatterns = [
      // Pattern: "text View here: /path"
      /(.*?)(View here: )(\/.*)/,
      // Pattern: "text [link text](path)"
      /(.*?)\[([^\]]+)\]\((\/[^)]+)\)/,
      // Pattern: "text <link>path</link>"
      /(.*?)<link>([^<]+)<\/link>/,
    ];

    for (const pattern of linkPatterns) {
      const match = msg.match(pattern);
      if (match) {
        const [, beforeLink, linkText, linkPath] = match;
        return (
          <>
            {beforeLink}
            <Link href={linkPath} color="inherit" underline="hover" sx={{ fontWeight: 'bold' }}>
              {linkText}
            </Link>
          </>
        );
      }
    }

    // If no link pattern found, return the original message
    return msg;
  };

  return (
    <Snackbar
      className="react-MessageToast"
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideUpTransition}
      sx={{
        zIndex: 9999, // Ensure toast appears above all other elements
      }}
    >
      <Alert
        onClose={onClose}
        severity={type}
        sx={{
          width: '100%',
          animation: open ? `${popAnimation} 0.4s ease-out` : 'none',
          boxShadow: 2, // Subtle shadow
        }}
      >
        {renderMessage(message)}
      </Alert>
    </Snackbar>
  );
};

export default MessageToast;
